import { faker } from '@faker-js/faker/locale/en'
import startCase from 'lodash-es/startCase'
import { DateTime } from 'luxon'
import type { Appointment } from './appointments'
import { GateTransaction } from './gateTransaction'
import { ImportStatus } from './importStatus'
import { LineImportStatus } from './lineImportStatus'
import type { LineLFDGap, LineLFDGapConfig } from './lineLFDGap'
import { lineWithholdsLFD, needsLineLFDCheck } from './lineLFDGap'
import { CleanTruckFee, PierPass } from './pierPass'
import type { ContainerFilters } from '~/compositions/containerFilters'
import type { ContainerStatusFilter } from '~/constants'
import { BRAND_NAME } from '~/constants/branding'
import { NO_CUSTOMER_ID } from '~/constants/customers'
import type {
  AutoBookRequestStatus,
  ContainerType,
  GETContainer,
  GETContainerBasic,
  GETContainerBasicWithAutoBook,
  GETContainerLineAndType,
  GETLocation,
  ParsedHold,
  PortComplex,
  SavedVesselForContainer,
  SimpleUser,
} from '~/services/apiClient'
import {
  CONTAINER_DISPLAY_STATES_BY_STATE,
  getPortalLabel,
  getTerminalLabel,
  OAKLAND_TERMINALS_SET,
  SHIPPERS_TERMINALS,
  TERMINAL_NOT_REQUIRING_APPOINTMENTS,
} from '~/constants'
import { shiftStartFromTime } from '~/logic/shifts'
import {
  AppointmentStatus,
  ContainerWatchState,
  HoldType,
  ImportLocation,
  PortalName,
  ShippingLine,
  TerminalName,
} from '~/services/apiClient'
import { parseAppointments } from '~/stores/appointments'
import {
  fastParseISO,
  formatDTMilitary,
  parseContainerNumbers,
  shortenContainerType,
} from '~/utils'
import { getHoldDescription } from '~/utils/holdUtils'

function isUnnamedTbdHold(hold: ParsedHold): boolean {
  return !hold.type && hold.raw_type?.trim().toUpperCase() === 'TBD'
}

export interface ContainerBadgeInfo {
  message: string
  type: 'info' | 'warning' | 'danger' | 'light-purple'
  tooltip?: string
  link?: string
  isTag?: boolean
}

export type ContainerState =
  | 'Import Tracking'
  | 'Ready for Appointment'
  | 'Appointment'
  | 'Out-Gated'
  | 'In-Gated'

export interface Action {
  label: 'Booking' | 'Booking Failed' | 'Just Booked'
  status: 'success' | 'warning' | 'danger'
  tooltip: string
}

/**
 * What `/containers/line_and_type/` knows about a container nobody here tracks. A
 * suggestion, never a fact: the line is the less reliable half on older data.
 */
export class ContainerLineAndType {
  number: string
  shipping_line?: ShippingLine
  container_type?: ContainerType
  last_seen: DateTime

  constructor(data: GETContainerLineAndType) {
    this.number = data.container_number
    // The API sends null for "unknown" but the generated client types it as optional
    this.shipping_line = data.shipping_line ?? undefined
    this.container_type = data.container_type ?? undefined
    this.last_seen = fastParseISO(data.last_seen)
  }
}

export class BasicContainer {
  number: string
  container_type?: ContainerType
  shipping_line?: ShippingLine
  last_related_terminal?: TerminalName
  cycle_state: ContainerWatchState
  import_out_gated_time?: DateTime
  empty_appointment_time?: DateTime
  empty_in_gated_time?: DateTime
  current_location?: GETLocation
  customer_id?: number
  customer_name?: string
  tags: string[]
  assigned_users: SimpleUser[]
  // Empty auto-book status, only populated when loaded via the
  // with_unwatched/with_auto_book/ endpoint (Empties list). The
  // lighter /basic/ endpoints leave it undefined.
  auto_book_empty_request_status?: AutoBookRequestStatus
  // True when the empty auto-book request is flagged hot (ignores capacity
  // settings). Same population rules as the status above.
  auto_book_empty_hot?: boolean
  constructor(
    container: GETContainerBasic | GETContainerBasicWithAutoBook,
    demoMode: boolean
  ) {
    this.number = container.container_number
    this.container_type = container.container_type
    this.shipping_line = container.shipping_line
    this.last_related_terminal = container.last_related_terminal
    this.cycle_state = container.cycle_state
    this.import_out_gated_time = container.import_out_gated_time
      ? fastParseISO(container.import_out_gated_time)
      : undefined
    this.empty_appointment_time = container.empty_appointment_time
      ? fastParseISO(container.empty_appointment_time)
      : undefined
    this.empty_in_gated_time = container.empty_in_gated_time
      ? fastParseISO(container.empty_in_gated_time)
      : undefined
    if (container.customer) {
      this.customer_id = container.customer.id
      if (demoMode) {
        this.customer_name = faker.company.name()
      } else {
        this.customer_name = container.customer.name
      }
    }
    this.current_location = container.current_location
    this.tags = container.tags
    this.assigned_users = container.assigned_users ?? []
    this.auto_book_empty_request_status =
      'auto_book_empty_request_status' in container
        ? container.auto_book_empty_request_status
        : undefined
    this.auto_book_empty_hot =
      'auto_book_empty_hot' in container
        ? container.auto_book_empty_hot
        : undefined
  }

  get out_gated_days(): number | null {
    if (this.import_out_gated_time) {
      const start = this.import_out_gated_time.startOf('day')
      // Use empty_in_gated_time when available so returned containers show the
      // actual duration they were out, not days-since-out-gate to today.
      const end = (this.empty_in_gated_time ?? DateTime.now()).startOf('day')
      const days = end.diff(start).as('days')
      return Math.floor(days) + 1
    }
    return null
  }

  get isMissingLine(): boolean {
    return !this.shipping_line
  }

  get isMissingType(): boolean {
    return !this.container_type
  }

  get isMissingLineOrType(): boolean {
    return this.isMissingLine || this.isMissingType
  }
}
function parseDT(dt: string | undefined): DateTime | undefined {
  if (dt) {
    return fastParseISO(dt)
  }
}
/**
 * States in which the container row renders its own inline LFD field (pre-pickup).
 *
 * Deliberately narrower than the backend's `lfd_check_states`, which covers everywhere
 * an LFD is shown *anywhere* — the drawer's per-source panel shows one post-outgate
 * too. The two are not duplicates of each other: this one is about the row's layout,
 * that one about how far the missing-line-LFD caveat has to reach.
 */
const LFD_STATES: Set<ContainerWatchState> = new Set([
  ContainerWatchState.ImportTracking,
  ContainerWatchState.ImportApptBooking,
  ContainerWatchState.ImportApptBooked,
])

function resolveBestMasterBL(
  c: Container
): { value: string; source: string } | undefined {
  if (c.import_status?.master_bill_of_lading) {
    return {
      value: c.import_status.master_bill_of_lading,
      source: getPortalLabel(c.import_status.source),
    }
  }
  for (const importStatus of c.import_statuses) {
    if (
      importStatus.master_bill_of_lading &&
      importStatus.source !== PortalName.PortOptimizer
    ) {
      return {
        value: importStatus.master_bill_of_lading,
        source: getPortalLabel(importStatus.source),
      }
    }
  }
  if (c.line_import_status?.master_bill_of_lading) {
    return {
      value: c.line_import_status.master_bill_of_lading,
      source: getPortalLabel(c.line_import_status.source),
    }
  }
  if (c.master_bill_of_lading) {
    return { value: c.master_bill_of_lading, source: 'User' }
  }
}

/**
 * The two fields `TagDialog` needs. Structural on purpose — the call sites hand it
 * different models (`Container` from Imports, `BasicContainer` from the Empties list)
 * and the dialog only ever reads these two.
 */
export type TaggableContainer = Pick<Container, 'number' | 'tags'>

export class Container {
  number: string
  tags: string[]
  terminal: TerminalName | null
  // Typed as the payload it is, rather than `object`, so it can be re-parsed (see
  // `containerStore.reparseContainers`).
  raw_data: GETContainer
  line_import_status: LineImportStatus | null
  import_status: ImportStatus | null
  import_statuses: ImportStatus[]
  pier_pass: PierPass | null = null
  clean_truck_fee: CleanTruckFee | null = null
  latest_appointment: Appointment | null
  other_appointment_counts_by_status: Map<AppointmentStatus, number>
  inbound_gate_transaction: GateTransaction | null
  outbound_gate_transaction: GateTransaction | null
  cycle_state: ContainerWatchState
  stateDescription: string
  status?: ContainerStatusFilter
  master_bill_of_lading?: string
  customer_id?: number
  customer_name: string
  line: ShippingLine | undefined
  type: ContainerType | undefined
  raw_type: string | undefined
  raw_line: string | undefined
  added_by_user?: SimpleUser
  assigned_users: SimpleUser[]
  auto_book_on: boolean
  auto_book_ignoring_capacity: boolean
  auto_book_request_status?: AutoBookRequestStatus
  watched_time: DateTime
  discharged_time?: DateTime
  import_appointment_time?: DateTime
  import_out_gated_time?: DateTime
  empty_appointment_time?: DateTime
  empty_in_gated_time?: DateTime
  last_free_day?: DateTime | null
  /**
   * The line's own LFD, where we have one from any source. Distinct from
   * `last_free_day`, which is the earlier of the terminal's and the line's and is
   * what we display; this one only answers whether the line's date is known at all.
   */
  line_last_free_day?: DateTime
  matched_vessel: SavedVesselForContainer | undefined
  raw_vessel: string | undefined
  vessel_name: string | undefined
  vessel_eta: DateTime | undefined
  // Derived fields
  is_in_on_vessel_state: boolean
  is_in_unavailable_state: boolean
  is_in_import_booking_state: boolean
  is_in_import_booked_state: boolean
  is_in_booked_state: boolean
  is_in_out_gated_state: boolean
  is_in_empty_booked_state: boolean
  is_in_empty_returned_state: boolean
  auto_booking_applies: boolean
  import_tracking_applies: boolean
  import_booking_applies: boolean
  shouldHaveLFD: boolean
  /**
   * The gap config for this container's line, if its line withholds its LFD.
   *
   * Handed in at construction rather than read from `shippingLinesStore` here: the
   * badges below are built eagerly in the constructor, so a getter reaching for the
   * store would still be reading it too late. Same reasoning as
   * `matchesGreenTruckFilter` in the container store — resolving this needs a store,
   * and a model has no business reaching into one.
   */
  lineLFDGap: LineLFDGap | undefined
  badges: ContainerBadgeInfo[]
  type_short: string | undefined
  out_gated_days: number | null
  booked_appointment: Appointment | null
  has_active_appointment: boolean
  can_mark_as_terminated: boolean
  scheduled_shift_name: string | null
  scheduledShiftStart: DateTime | null
  terminal_label: string
  tms_reference: string | null
  note_body: string | null
  // This is to keep track of when the frontend receives container updates and to update
  // views appropriately.
  frontendVersion: number
  key: string
  rowHeight: number
  port_complex: PortComplex

  constructor(
    container: GETContainer,
    demoMode: boolean,
    lineLFDGapConfig: LineLFDGapConfig
  ) {
    this.number = container.container_number
    if (demoMode && container.tags.length > 0) {
      this.tags = [faker.commerce.productName()]
    } else {
      this.tags = container.tags
    }
    this.terminal = container.last_related_terminal || null
    this.raw_data = container
    this.line_import_status = container.line_import_status
      ? new LineImportStatus(container.line_import_status)
      : null
    this.import_statuses = container.import_statuses.map(
      (import_status) => new ImportStatus(import_status)
    )
    this.import_status =
      this.import_statuses.length > 0 ? this.import_statuses[0] : null
    this.line = container.shipping_line
    this.type = container.container_type
    for (const importStatus of this.import_statuses) {
      this.line = this.line ?? importStatus.parsed_shipping_line
      this.raw_line = importStatus.shipping_line
      this.type = this.type ?? importStatus.parsed_container_type
      this.raw_type = importStatus.container_type
    }
    if (container.pier_pass) {
      this.pier_pass = new PierPass(container.pier_pass)
    }
    if (container.clean_truck_fee) {
      this.clean_truck_fee = new CleanTruckFee(container.clean_truck_fee)
    }
    this.latest_appointment = container.latest_appointment
      ? parseAppointments([container.latest_appointment], demoMode)[0]
      : null
    this.other_appointment_counts_by_status = new Map(
      container.other_appointment_counts.map((count) => [
        count.status,
        count.count,
      ])
    )
    this.inbound_gate_transaction = container.inbound_gate_transaction
      ? new GateTransaction(container.inbound_gate_transaction, demoMode)
      : null
    this.outbound_gate_transaction = container.outbound_gate_transaction
      ? new GateTransaction(container.outbound_gate_transaction, demoMode)
      : null
    this.cycle_state = container.cycle_state
    this.status = CONTAINER_DISPLAY_STATES_BY_STATE.get(this.cycle_state)
    if (this.cycle_state === ContainerWatchState.ImportEmptyApptBooked) {
      // Kinda hacky by we want to be more specific for this case and not just show
      // "Appointed"
      this.stateDescription = 'Empty Appointed'
    } else if (this.cycle_state === ContainerWatchState.ImportEmpty) {
      // ImportEmpty shares the "Out-Gated" tab (see CONTAINER_STATES), but show the more
      // specific "Empty" label so a marked-empty container isn't mislabeled as "Out-Gated".
      this.stateDescription = 'Empty'
    } else if (
      this.cycle_state === ContainerWatchState.ImportPickedUp &&
      this.import_status &&
      this.import_status.parsed_location === ImportLocation.Rail
    ) {
      this.stateDescription = 'On Rail'
    } else {
      this.stateDescription = this.status?.label ?? startCase(this.cycle_state)
    }
    this.port_complex = container.port_complex

    this.master_bill_of_lading = container.master_bl
    this.tms_reference = container.tms_reference || null
    this.note_body = container.note_body ?? null
    if (demoMode) {
      this.customer_name = faker.company.name()
      this.added_by_user = {
        id: 1,
        first_name: faker.person.firstName(),
        last_name: faker.person.lastName(),
      }
      this.assigned_users = [
        {
          id: 1,
          first_name: faker.person.firstName(),
          last_name: faker.person.lastName(),
        },
      ]
    } else {
      this.added_by_user = container.added_by_user
      this.customer_name = container.customer?.name
        ? container.customer.name
        : 'No Customer'
      this.customer_id = container.customer?.id
      this.assigned_users = container.assigned_users
    }
    this.matched_vessel = container.matched_vessel
    this.raw_vessel = container.vessel
    this.vessel_name = container.matched_vessel?.name || container.vessel
    // fastParseISO handles both date-only and full ISO formats
    this.vessel_eta = container.vessel_eta
      ? fastParseISO(container.vessel_eta).startOf('day')
      : undefined
    this.auto_book_on = container.auto_book_on
    this.auto_book_ignoring_capacity = container.auto_book_ignoring_capacity
    this.auto_book_request_status = container.auto_book_request_status
    this.frontendVersion = 0
    this.key = this._updateKey()
    this.watched_time = fastParseISO(container.watched_time)
    this.discharged_time = parseDT(container.discharged_time)
    this.import_appointment_time = parseDT(container.import_appointment_time)
    this.import_out_gated_time = parseDT(container.import_out_gated_time)
    this.empty_appointment_time = parseDT(container.empty_appointment_time)
    this.empty_in_gated_time = parseDT(container.empty_in_gated_time)
    this.last_free_day =
      parseDT(container.last_free_date) ||
      parseDT(container.import_status?.last_free_date)
    this.line_last_free_day = parseDT(container.line_last_free_date)
    this.is_in_on_vessel_state =
      this.cycle_state === ContainerWatchState.ImportTrackingOnVessel
    this.is_in_unavailable_state =
      this.cycle_state === ContainerWatchState.ImportTracking
    this.is_in_import_booking_state =
      this.cycle_state === ContainerWatchState.ImportApptBooking
    this.is_in_import_booked_state =
      this.cycle_state === ContainerWatchState.ImportApptBooked
    this.is_in_out_gated_state =
      container.cycle_state === ContainerWatchState.ImportPickedUp
    this.is_in_empty_booked_state =
      this.cycle_state === ContainerWatchState.ImportEmptyApptBooked
    this.is_in_empty_returned_state =
      this.cycle_state === ContainerWatchState.ImportEmptyReturned

    // General booked state (empty or import)
    this.is_in_booked_state =
      this.is_in_import_booked_state || this.is_in_empty_booked_state
    this.auto_booking_applies =
      this.cycle_state === ContainerWatchState.ImportTracking ||
      this.cycle_state === ContainerWatchState.ImportTrackingOnVessel
    this.import_booking_applies =
      this.cycle_state === ContainerWatchState.ImportApptBooked ||
      this.is_in_import_booking_state

    this.import_tracking_applies =
      this.import_booking_applies ||
      this.cycle_state === ContainerWatchState.ImportTracking ||
      this.cycle_state === ContainerWatchState.ImportTrackingOnVessel

    this.shouldHaveLFD = LFD_STATES.has(this.cycle_state)
    this.lineLFDGap = this.line ? lineLFDGapConfig.get(this.line) : undefined
    this.badges = this.calcBadges()
    this.type_short = shortenContainerType(this.type)
    this.out_gated_days = this.calcOutGatedDays()
    this.booked_appointment = this.calcBookedAppointment()
    this.has_active_appointment = Boolean(this.booked_appointment)
    this.can_mark_as_terminated =
      this.is_in_out_gated_state ||
      this.cycle_state === ContainerWatchState.ImportEmpty ||
      this.cycle_state === ContainerWatchState.Unknown
    this.scheduled_shift_name = this.calcScheduledShiftName()
    this.scheduledShiftStart = this.booked_appointment
      ? shiftStartFromTime(this.booked_appointment.window_start)
      : null
    this.terminal_label = this.calcTerminalLabel()
    this.rowHeight = this.calcRowHeight()
  }

  get isOaklandTerminal(): boolean {
    if (this.terminal && OAKLAND_TERMINALS_SET.has(this.terminal)) {
      return true
    }
    return false
  }

  /**
   * Returns the import status only when the container is in an active import
   * tracking phase (on vessel, available, or booked). Returns null for containers
   * in the empty phase (out-gated, empty, empty appointed, empty returned) since
   * import status data like hazmat/wheeled is irrelevant once a container is empty.
   */
  get relevantImportStatus(): ImportStatus | null {
    if (!this.import_tracking_applies) return null
    return this.import_status
  }

  /**
   * Returns true if any import status has hazardous set, and the container is
   * in an active import tracking phase. Checks all import_statuses rather than
   * just the primary one, since multiple sources (e.g. tti_no_login, eModal)
   * may each independently report hazardous status.
   */
  get isHazardous(): boolean {
    if (!this.import_tracking_applies) return false
    return this.import_statuses.some((s) => Boolean(s.hazardous))
  }

  get bestMasterBL(): string | undefined {
    return resolveBestMasterBL(this)?.value
  }

  get bestMasterBLSource(): string | undefined {
    return resolveBestMasterBL(this)?.source
  }

  get showMiddleRow(): boolean {
    return Boolean(
      this.badges.length > 0 ||
      this.is_in_empty_returned_state ||
      this.is_in_out_gated_state ||
      this.booked_appointment
    )
  }

  // A single empty-return appointment that can be rescheduled on its own.
  // Dual moves (empty-in linked to a load-out) are excluded — they reschedule
  // through the separate add-empty/swap mechanism, not the empty-reschedule flow.
  get is_single_empty_booked_state(): boolean {
    return this.is_in_empty_booked_state && !this.booked_appointment?.is_dual
  }

  get terminalBlockAvailabilityKey(): string | null {
    if (this.import_booking_applies) {
      if (this.import_status?.terminal_block_availability_key) {
        return this.import_status.terminal_block_availability_key
      }
    }
    // iterate over import_statuses and find the first one that has a terminal_block_availability_key
    for (const importStatus of this.import_statuses) {
      if (importStatus.terminal_block_availability_key) {
        return importStatus.terminal_block_availability_key
      }
    }
    return null
  }

  get lfdIsRelevant(): boolean {
    return (
      // On vessel would only be relevant if they had for some reason set a LFD, which
      // normally they do not
      (this.is_in_on_vessel_state && !!this.last_free_day) ||
      this.is_in_unavailable_state ||
      this.is_in_import_booking_state ||
      this.is_in_import_booked_state
    )
  }

  get lfdInfo(): string | undefined {
    // find other lfd's in other_lfds
    const lfdDescriptions: string[] = []
    if (this.uniqueDateCount <= 1) {
      return undefined
    }
    if (this.line_import_status && this.line_import_status.last_free_day) {
      let desc = this.line_import_status.parsed_shipping_line
        ? this.line_import_status.parsed_shipping_line
        : ''
      desc = `${desc}_line_last_free_day`
      lfdDescriptions.push(
        `${this.prettifyLFDDescription(desc)}: ${this.line_import_status.last_free_day.toFormat('yyyy-MM-dd')}`
      )
    }
    if (this.import_status) {
      const addUniqueDescription = (desc: string) => {
        if (!lfdDescriptions.includes(desc)) {
          lfdDescriptions.push(desc)
        }
      }

      for (const lfd of this.import_status.other_last_free_dates) {
        addUniqueDescription(
          `${this.prettifyLFDDescription(lfd.description)}: ${lfd.date}`
        )
      }
      if (this.import_status.last_free_day) {
        addUniqueDescription(
          `${this.prettifyLFDDescription(this.import_status.sourceLabel)}: ${this.import_status.last_free_day.toFormat('yyyy-MM-dd')}`
        )
      }
    }
    // join the lfd descriptions with a comma
    return lfdDescriptions.join(', ')
  }

  get mainLfdSource(): string | undefined {
    const sourceText = 'Source: '
    if (this.last_free_day) {
      if (
        this.line_import_status &&
        this.line_import_status.last_free_day &&
        this.line_import_status.last_free_day.toFormat('yyyy-MM-dd') ===
          this.last_free_day.toFormat('yyyy-MM-dd')
      ) {
        return sourceText + this.line_import_status.sourceLabel
      }
      if (
        this.import_status &&
        this.import_status.last_free_day &&
        this.import_status.last_free_day.toFormat('yyyy-MM-dd') ===
          this.last_free_day.toFormat('yyyy-MM-dd')
      ) {
        return sourceText + this.import_status.sourceLabel
      }
    }
    return undefined
  }

  get uniqueDateCount(): number {
    const uniqueDates = new Set<string>()
    if (this.line_import_status && this.line_import_status.last_free_day) {
      uniqueDates.add(
        this.line_import_status.last_free_day.toFormat('yyyy-MM-dd')
      )
    }
    if (this.last_free_day != null) {
      uniqueDates.add(this.last_free_day.toFormat('yyyy-MM-dd'))
    }
    if (this.import_status) {
      if (this.import_status.last_free_day != null) {
        uniqueDates.add(this.import_status.last_free_day.toFormat('yyyy-MM-dd'))
      }
      for (const lfd of this.import_status.other_last_free_dates) {
        let date = lfd.date.trim()
        date.replaceAll('/', '-')
        if (date.length === 5 && this.import_status.last_free_day) {
          date = `${this.import_status.last_free_day.year}-${date}`
        } else if (date.length === 5) {
          continue // skip this as it isn't formatted and we can't determine the year
        }
        uniqueDates.add(date)
      }
    }
    return uniqueDates.size
  }

  /**
   * Caveat for a container whose line withholds its LFD and for which we have no
   * line LFD from any source — the tooltip, and a lookup link where one exists.
   *
   * Deliberately not gated on terminal or cycle state. The warning has to reach
   * every place we render an LFD, and `ImportStatusDisplay` renders one in any
   * state; a parallel state list is what let the badge drift away from the
   * displayed date in DEV-7154. Callers that only display an LFD in some states
   * (`ContainerRow`, `ContainerDetailsHeader`) already gate themselves.
   */
  get missingLineLFDInfo(): { link?: string; tooltip: string } | undefined {
    // `line_last_free_day` is the backend's own answer to "do we have one, from any
    // source?" (the dedicated line_import_status, or a terminal status carrying the
    // SSL's LFD, as eModal does). It replaces a hand-rolled scan over import_statuses
    // here, which could disagree with the `needs_line_lfd_check` filter that runs off
    // the stored value.
    if (!lineWithholdsLFD(this.lineLFDGap, this.line_last_free_day)) {
      return undefined
    }

    return {
      tooltip: this.lineLFDGap!.note,
      link: this.lineLFDGap!.lookupUrl,
    }
  }

  /**
   * Whether this is a container the user should go and check the line's LFD for.
   *
   * The frontend half of the backend's `needs_line_lfd_check` filter, evaluated off
   * the same config and the same state list, so a badge always means the container
   * is in the filtered set and vice versa.
   */
  get needsLineLFDCheck(): boolean {
    return needsLineLFDCheck(
      this.lineLFDGap,
      this.cycle_state,
      this.line_last_free_day
    )
  }

  prettifyLFDDescription(desc: string) {
    desc = desc.replaceAll('_', ' ')
    return desc
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Just an alias
  get container_number(): string {
    return this.number
  }

  get container_type(): ContainerType | null {
    return this.type ?? null
  }

  calcRowHeight(): number {
    let height = 60
    if (this.showMiddleRow) {
      height += 24
    }
    return height
  }

  calcOutGatedDays(): number | null {
    if (this.import_out_gated_time) {
      const start = this.import_out_gated_time.startOf('day')
      let end
      if (this.empty_in_gated_time) {
        end = this.empty_in_gated_time
      } else {
        end = DateTime.now()
      }
      // I've been advised that what matters is the "real" calendar day of the gate
      // transaction, not the "shift date"
      end = end.startOf('day')
      const days = end.diff(start).as('days')

      // We add one because both start and end calendars days are included in the count
      return Math.floor(days) + 1
    }
    return null
  }

  calcBookedAppointment(): Appointment | null {
    if (
      this.latest_appointment &&
      this.is_in_booked_state &&
      (this.latest_appointment.status === AppointmentStatus.Scheduled ||
        this.latest_appointment.status === AppointmentStatus.InProgress ||
        this.latest_appointment.status === AppointmentStatus.Completed)
    ) {
      return this.latest_appointment
    } else {
      return null
    }
  }

  calcScheduledShiftName(): string | null {
    if (this.booked_appointment) {
      return this.booked_appointment.scheduledShift
    } else {
      return null
    }
  }

  calcTerminalLabel(): string {
    if (this.terminal) {
      return getTerminalLabel(this.terminal)
    } else {
      return '-'
    }
  }

  freshTtiNamedHolds(terminal: TerminalName): {
    holds: ParsedHold[]
    sourceLabel: string
  } | null {
    const ttiStatuses = this.import_statuses.filter(
      (status) =>
        status.source === PortalName.TtiNoLogin &&
        status.terminal === terminal &&
        !status.isStale
    )
    for (const ttiStatus of ttiStatuses) {
      const namedHolds = ttiStatus.parsed_holds.filter(
        (hold) => hold.type && !this.usesFeeHoldBadge(hold)
      )
      if (namedHolds.length > 0) {
        return { holds: namedHolds, sourceLabel: ttiStatus.sourceLabel }
      }
    }
    return null
  }

  // TMF always, and CTF when PortCheck already owns it, keep their own badges.
  usesFeeHoldBadge(hold: ParsedHold): boolean {
    if (hold.type === HoldType.TmfHold) {
      return true
    }
    return (
      hold.type === HoldType.CtfHold &&
      this.clean_truck_fee !== null &&
      this.clean_truck_fee.has_hold === true
    )
  }

  appendHoldBadge(
    badges: ContainerBadgeInfo[],
    hold: ParsedHold,
    sourceLabel: string
  ): void {
    if (this.usesFeeHoldBadge(hold)) {
      return
    }
    badges.push({
      message: getHoldDescription(hold),
      type: 'warning',
      tooltip: `Raw: ${hold.raw_type}. Found on ${sourceLabel}`,
    })
  }

  calcBadges(): ContainerBadgeInfo[] {
    const badges: ContainerBadgeInfo[] = this.getTagBadges()
    const maybeBadges = [
      this.getStaleAppointmentBadges(),
      this.getStaleImportStatusesBadges(),
      this.getAPMDualBadge(),
      this.getUpcomingAppointmentBadge(),
      this.getClosedAreaBadge(),
      this.getReadyForPickupBadge(),
      this.getMissedAppointmentBadge(),
      this.getAppointmentNotRequiredBadge(),
      this.getShippersBadge(),
      this.getAPMNoBolBadge(),
      this.getBookedOffDrayDogBadge(),
      this.getLineLFDWarningBadge(),
      this.getBOLNeededForCoscoBadge(),
      this.getWbctImportBolNeededBadge(),
      this.getStaleScheduledAppointmentBadge(),
      this.getWbctWheeledNoAppointmentRequiredBadge(),
    ]
    for (const maybeBadge of maybeBadges) {
      if (maybeBadge) {
        badges.push(maybeBadge)
      }
    }
    badges.push(...this.getPierPassBadges())
    const relevantImportStatus = this.relevantImportStatus
    if (relevantImportStatus) {
      // eModal's other-hold text is TBD (code TH). It does not name the hold.
      // The TTI site scrape does. Show that name when the scrape is still fresh.
      let replacedTbd = false
      const ttiHolds = this.freshTtiNamedHolds(relevantImportStatus.terminal)
      for (const hold of relevantImportStatus.parsed_holds) {
        if (isUnnamedTbdHold(hold)) {
          if (ttiHolds) {
            if (!replacedTbd) {
              const shownTypes = new Set(
                relevantImportStatus.parsed_holds.flatMap((existing) =>
                  existing.type ? [existing.type] : []
                )
              )
              for (const ttiHold of ttiHolds.holds) {
                if (!ttiHold.type || shownTypes.has(ttiHold.type)) {
                  continue
                }
                shownTypes.add(ttiHold.type)
                this.appendHoldBadge(badges, ttiHold, ttiHolds.sourceLabel)
              }
              replacedTbd = true
            }
            continue
          }
        }
        this.appendHoldBadge(badges, hold, relevantImportStatus.sourceLabel)
      }
      const demurrageWarning = this.getImportStatusDemurrageBadges()
      if (demurrageWarning) {
        badges.push(demurrageWarning)
      }
    }
    if (this.is_in_empty_returned_state) {
      badges.push(...this.getTerminatedBadges())
    }
    return badges
  }

  getBOLNeededForCoscoBadge(): ContainerBadgeInfo | undefined {
    if (this.line === ShippingLine.Cosu && !this.bestMasterBL) {
      return {
        message: 'BOL Needed for Cosco',
        type: 'warning',
        tooltip:
          'BOL not found but needed for Cosco tracking and to determine line LFD. ' +
          'Add it in container details or Add Containers dialog.',
      }
    }
  }

  /** WBCT import containers require Master BOL for Auto Book; show when missing. */
  getWbctImportBolNeededBadge(): ContainerBadgeInfo | undefined {
    const wbctImportStates: ContainerWatchState[] = [
      ContainerWatchState.ImportTracking,
      ContainerWatchState.ImportTrackingOnVessel,
      ContainerWatchState.ImportApptBooking,
      ContainerWatchState.ImportApptBooked,
    ]
    if (
      this.terminal === TerminalName.Wbct &&
      wbctImportStates.includes(this.cycle_state) &&
      this.import_status &&
      !this.bestMasterBL
    ) {
      return {
        message: 'BOL Needed for booking',
        type: 'warning',
        tooltip:
          'WBCT requires Master BOL for booking. This container is missing the BOL, and it will block Auto Book. Add BOL in container details or Add Containers dialog.',
      }
    }
  }

  getStaleImportStatusesBadges(): ContainerBadgeInfo | undefined {
    if (
      this.import_tracking_applies &&
      this.import_status &&
      this.import_status.isStale
    ) {
      return {
        message: `Data fetch failure`,
        type: 'danger',
        tooltip: this.import_status.staleDescription,
      }
    }
  }

  getStaleAppointmentBadges(): ContainerBadgeInfo | undefined {
    if (
      this.is_in_booked_state &&
      this.latest_appointment &&
      (this.latest_appointment.isScheduled ||
        this.latest_appointment.isInProgress) &&
      this.latest_appointment.isStale
    ) {
      const portalDesc = this.latest_appointment.portal_name
      const lastSeenDesc = formatDTMilitary(
        this.latest_appointment.window_start
      )
      const lastSeenAgoDesc = this.latest_appointment.window_start.toRelative()
      return {
        message: `Appointment not found`,
        type: 'danger',
        tooltip:
          `We last saw this appointment on ${portalDesc} at ${lastSeenDesc} (${lastSeenAgoDesc}) but have not observed it since. ` +
          `You should double check directly with the appointment system, this appointment may have been deleted`,
      }
    }
  }

  getTagBadges(): ContainerBadgeInfo[] {
    const badges: ContainerBadgeInfo[] = []
    this.tags.forEach((tag) => {
      badges.push({
        message: tag,
        type: 'light-purple',
        tooltip: `Tag: ${tag}`,
        isTag: true,
      })
    })
    return badges
  }

  getImportStatusDemurrageBadges(): ContainerBadgeInfo | undefined {
    if (
      ![
        'import_tracking',
        'import_appt_booking',
        'import_appt_booked',
      ].includes(this.cycle_state) ||
      !this.import_status
    ) {
      return
    }
    if (
      this.import_status.demurrage_fees &&
      this.import_status.demurrage_fees > 0
    ) {
      const demurrageDesc =
        '$' + `${this.import_status.demurrage_fees.toLocaleString()}`
      return {
        message: demurrageDesc,
        type: 'danger',
        tooltip: `Found ${demurrageDesc} demurrage charge on ${
          this.import_status.sourceLabel
        }`,
      }
    }
  }

  getAppointmentNotRequiredBadge(): ContainerBadgeInfo | undefined {
    if (this.import_tracking_applies) {
      if (
        (this.terminal &&
          TERMINAL_NOT_REQUIRING_APPOINTMENTS.has(this.terminal)) ||
        (this.import_status?.extra_data &&
          'repo' in this.import_status.extra_data &&
          this.import_status.extra_data.repo !== null)
      ) {
        return {
          message: `No Appointment Required`,
          type: 'info',
          tooltip: `Container may not require an appointment, check repo.`,
        }
      }
    }
  }

  getWbctWheeledNoAppointmentRequiredBadge(): ContainerBadgeInfo | undefined {
    if (
      this.import_tracking_applies &&
      this.terminal === TerminalName.Wbct &&
      this.import_status &&
      this.import_status.wheeled === true
    ) {
      return {
        message: `No Appointment Required`,
        type: 'info',
        tooltip: `Wheeled containers do not require an appointment.`,
      }
    }
  }

  getShippersBadge(): ContainerBadgeInfo | undefined {
    const statusesToWarn: ContainerWatchState[] = [
      ContainerWatchState.ImportApptBooking,
      ContainerWatchState.ImportApptBooked,
      ContainerWatchState.ImportTracking,
      ContainerWatchState.ImportTrackingOnVessel,
    ]
    // any of the terminals in this.import_statuses
    const terminals = this.import_statuses.map((status) => status.terminal)
    if (statusesToWarn.includes(this.cycle_state)) {
      if (this.terminal && !SHIPPERS_TERMINALS.has(this.terminal)) {
        if (
          (this.import_status?.extra_data &&
            'repo' in this.import_status.extra_data &&
            this.import_status.extra_data.repo !== null) ||
          terminals.some((terminal) => SHIPPERS_TERMINALS.has(terminal))
        ) {
          return {
            message: `Shippers`,
            type: 'info',
            tooltip: `Container may have moved to shippers`,
          }
        }
      }
    }
  }

  getAPMDualBadge(): ContainerBadgeInfo | undefined {
    if (
      this.terminal === TerminalName.Apm &&
      this.cycle_state === ContainerWatchState.ImportApptBooked &&
      this.latest_appointment &&
      this.latest_appointment.linked_appointments.length === 0 &&
      this.latest_appointment.extra_data
    ) {
      if (
        Object.hasOwn(
          this.latest_appointment.extra_data,
          'dual_empty_placeholder'
        )
      ) {
        return {
          message: '✅ Empty Placeholder',
          type: 'info',
          tooltip:
            '"ADD EMPTY DROPOFF" was checked when booking, so there is an empty placeholder appointment and any accepted container type should be able to be added as an empty',
        }
      } else {
        return {
          message: '❌ No Empty Placeholder',
          type: 'info',
          tooltip:
            'There is no empty placeholder on for this appointment, there will have to be an empty appointment available for whatever empty container you would like to add',
        }
      }
    }
  }

  getAPMNoBolBadge(): ContainerBadgeInfo | undefined {
    const statusesToWarn: ContainerWatchState[] = [
      ContainerWatchState.ImportApptBooking,
      ContainerWatchState.ImportApptBooked,
      ContainerWatchState.ImportTracking,
      ContainerWatchState.ImportTrackingOnVessel,
    ]
    if (
      this.terminal === TerminalName.Apm &&
      statusesToWarn.includes(this.cycle_state) &&
      this.import_status &&
      this.import_status.master_bill_of_lading === null
    ) {
      return {
        message: '⛔️ APM Missing BOL',
        type: 'warning',
        tooltip:
          'BOL not found on import status but may be required for booking. Please check with the terminal.',
      }
    }
  }

  getLineLFDWarningBadge(): ContainerBadgeInfo | undefined {
    if (this.needsLineLFDCheck && this.missingLineLFDInfo) {
      return {
        message: `CHECK LINE FOR LFD`,
        // Stays `danger`: this badge has been red since 2024 and users already read
        // it at a glance. Recolouring it at the same time as widening it to Maersk
        // would make a familiar signal look like a new one.
        type: 'danger',
        tooltip: this.missingLineLFDInfo.tooltip,
        link: this.missingLineLFDInfo.link,
      }
    }
  }

  getBookedOffDrayDogBadge(): ContainerBadgeInfo | undefined {
    if (
      (this.cycle_state === ContainerWatchState.ImportApptBooked ||
        this.cycle_state === ContainerWatchState.ImportPickedUp ||
        this.cycle_state === ContainerWatchState.ImportEmptyApptBooked) &&
      this.latest_appointment &&
      !this.latest_appointment.booked_on_dray_dog
    ) {
      return {
        message: `Not booked by ${BRAND_NAME}`,
        type: 'warning',
        tooltip:
          `Appointment booked directly with ${this.latest_appointment.portal_name} (not using ${BRAND_NAME}).` +
          ` Book via ${BRAND_NAME} for better tracking`,
      }
    }
  }

  getMissedAppointmentBadge(): ContainerBadgeInfo | undefined {
    if (!this.import_booking_applies) return
    let numMissedAppointments =
      this.other_appointment_counts_by_status.get(AppointmentStatus.Missed) ?? 0
    if (
      this.latest_appointment &&
      this.latest_appointment.status === AppointmentStatus.Missed
    ) {
      numMissedAppointments += 1
    }
    if (numMissedAppointments > 0) {
      return {
        message: `${numMissedAppointments} Missed`,
        type: 'danger',
        tooltip: `${numMissedAppointments} missed appointments`,
      }
    }
  }

  getStaleScheduledAppointmentBadge(): ContainerBadgeInfo | undefined {
    if (!this.is_in_import_booking_state) return
    if (
      this.latest_appointment &&
      this.latest_appointment.status === AppointmentStatus.Scheduled &&
      this.latest_appointment.is_import_out &&
      this.latest_appointment.window_start < DateTime.now() &&
      this.latest_appointment.window_start > DateTime.now().minus({ days: 1 })
    ) {
      const apptStart =
        this.latest_appointment.window_start.toFormat('yyyy-MM-dd HH:mm')
      const refToUse =
        this.latest_appointment.display_terminal_reference ||
        this.latest_appointment.terminal_reference
      return {
        message: `Stale Appt`,
        type: 'danger',
        tooltip:
          `Scheduled appointment ` +
          `${refToUse} with start time ${apptStart} is in the past`,
      }
    }
  }

  getTrapacWarningBadge(): ContainerBadgeInfo | undefined {
    const statusesToWarn: ContainerWatchState[] = [
      ContainerWatchState.ImportApptBooking,
      ContainerWatchState.ImportApptBooked,
      ContainerWatchState.ImportTracking,
      ContainerWatchState.ImportTrackingOnVessel,
    ]
    if (
      this.terminal === TerminalName.Trapac &&
      statusesToWarn.includes(this.cycle_state)
    ) {
      return {
        message: 'Reliability Issues (hover me)',
        type: 'danger',
        tooltip:
          'Trapac data unreliable right now. Consider checking the terminal website',
      }
    }
  }

  getClosedAreaBadge(): ContainerBadgeInfo | undefined {
    const importStatus = this.relevantImportStatus
    if (importStatus && importStatus.closed_area === true) {
      let link: string | undefined
      let tooltip = `This container is in a closed area`
      if (this.terminal === TerminalName.Yti) {
        tooltip += `. Check YTI Closed Area schedule to verify (click to view)`
        link = 'https://yti.com/terminal-schedules/closed-vessel-area-schedule/'
      }
      // This case is for TTI Unable To Locate (message: CONTAINER IN UNDEFINED LOCATION)
      if (
        importStatus.ready_for_appointment === false &&
        Object.hasOwn(importStatus.extra_data, 'availability_reason') &&
        // @ts-expect-error - extra_data shape not fully typed
        importStatus.extra_data.availability_reason
      ) {
        const reason: string =
          // @ts-expect-error - extra_data shape not fully typed
          importStatus.extra_data.availability_reason
        tooltip += `: ${reason}`
      }
      return {
        message: 'Closed Area',
        type: 'danger',
        tooltip,
        link,
      }
    }
  }

  getReadyForPickupBadge(): ContainerBadgeInfo | undefined {
    const statusesToWarn: ContainerWatchState[] = [
      ContainerWatchState.ImportApptBooking,
      ContainerWatchState.ImportApptBooked,
    ]
    if (
      statusesToWarn.includes(this.cycle_state) &&
      this.import_status &&
      this.import_status.available_for_pickup === false
    ) {
      return {
        message: 'Not Ready For Pickup',
        type:
          this.cycle_state === ContainerWatchState.ImportApptBooked
            ? 'danger' // "Not ready for pickup" is DANGEROUS if you have an appointment...
            : 'warning', // ...but not if you don't
        tooltip: `Not ready for pickup`,
      }
    }
  }

  getUpcomingAppointmentBadge(): ContainerBadgeInfo | undefined {
    if (this.booked_appointment) {
      if (
        this.booked_appointment.window_start &&
        this.booked_appointment.window_start < DateTime.now() &&
        this.booked_appointment.status !== 'in_progress'
      ) {
        return {
          message: '🚨 Appointment time reached',
          type: 'danger',
          tooltip:
            'Appointment start time has been reached, cancel if it will be missed!',
        }
      }
    }
  }

  getPierPassBadges(): ContainerBadgeInfo[] {
    const badges: ContainerBadgeInfo[] = []
    if (
      (this.cycle_state &&
        !(
          this.cycle_state === ContainerWatchState.ImportTracking ||
          this.cycle_state === ContainerWatchState.ImportTrackingOnVessel ||
          this.cycle_state === ContainerWatchState.ImportApptBooking ||
          this.cycle_state === ContainerWatchState.ImportApptBooked
        )) ||
      // Oakland doesn't have Pier Pass
      (this.terminal && OAKLAND_TERMINALS_SET.has(this.terminal))
    ) {
      return badges
    }
    badges.push(...this.getBadgesForFeeType(this.pier_pass, 'TMF'))
    badges.push(...this.getBadgesForFeeType(this.clean_truck_fee, 'CTF'))
    return badges
  }

  getBadgesForFeeType(
    pierPassObject: PierPass | null,
    feeName: string
  ): ContainerBadgeInfo[] {
    const warnings: ContainerBadgeInfo[] = []
    const portalLabel = feeName === 'TMF' ? 'PierPass' : 'PortCheck'

    if (feeName === 'TMF' && this.import_status) {
      const hasPierPassHold = pierPassObject?.has_hold ?? false
      const hasTerminalHold = this.import_status.parsed_holds.some(
        (hold) => hold.type === HoldType.TmfHold
      )

      if (hasPierPassHold && hasTerminalHold) {
        warnings.push({
          message: 'TMF',
          type: 'warning',
          tooltip: `Hold found on Pier Pass and ${this.import_status.sourceLabel}`,
        })
      } else if (hasPierPassHold && !hasTerminalHold) {
        warnings.push({
          message: 'TMF (Pier Pass)',
          type: 'warning',
          tooltip: `Hold cleared on ${this.import_status.sourceLabel}, but still showing in Pier Pass website`,
        })
      } else if (!hasPierPassHold && hasTerminalHold) {
        warnings.push({
          message: `TMF (${getTerminalLabel(this.import_status.terminal)})`,
          type: 'warning',
          tooltip: `Hold cleared on Pier Pass website, but still showing in ${this.import_status.sourceLabel}`,
        })
      }
    } else if (pierPassObject) {
      if (pierPassObject.has_hold) {
        warnings.push({
          message: `${portalLabel}: ${feeName}`,
          type: 'warning',
          tooltip: `Hold found on ${portalLabel} website`,
        })
      }
      if (pierPassObject.terminals.size > 1) {
        const terminalsDesc = [...pierPassObject.terminals.values()]
          .map(getTerminalLabel)
          .join(', ')
        warnings.push({
          message: `${portalLabel}: ${feeName} Multiple Terminals`,
          type: 'danger',
          tooltip: `Found on ${feeName} website - pay attention! Terminals: ${terminalsDesc}`,
        })
      }
      if (pierPassObject.terminals.size === 0) {
        warnings.push({
          message: `${portalLabel}: ${feeName} No Terminal Found`,
          type: 'warning',
          tooltip: `Found on ${feeName} website`,
        })
      }
    } else if (this.import_status && !pierPassObject) {
      warnings.push({
        message: `${portalLabel}: Unknown`,
        type: 'info',
        tooltip: `${feeName} status is pending/unknown or we could not find this container in the ${portalLabel} system`,
      })
    }
    return warnings
  }

  getTerminatedBadges(): ContainerBadgeInfo[] {
    const badges = [] as ContainerBadgeInfo[]
    if (this.is_in_empty_returned_state) {
      if (this.inbound_gate_transaction) {
        if (this.inbound_gate_transaction.truck_rfid) {
          badges.push({
            message: `RFID: ${this.inbound_gate_transaction.truck_rfid}`,
            type: 'info',
            tooltip: `RFID number of the terminating truck`,
          })
        }
        if (this.inbound_gate_transaction.truck_license_plate) {
          badges.push({
            message: `Plate: ${this.inbound_gate_transaction.truck_license_plate}`,
            type: 'info',
            tooltip: `License plate of the terminating truck`,
          })
        }
        if (this.inbound_gate_transaction.drivers_license_number) {
          badges.push({
            message: `Driver: ${this.inbound_gate_transaction.drivers_license_number}`,
            type: 'info',
            tooltip: `Commercial driver's licence number of the terminating driver`,
          })
        }
      }
    }
    return badges
  }

  // NOTE: `filter.is_green_truck` is deliberately absent here — resolving green status
  // needs the truck roster from a store, so the containers store applies it alongside
  // this call (`matchesGreenTruckFilter`).
  matchesFilters(filter: ContainerFilters): boolean {
    if (filter.states && !filter.states.includes(this.cycle_state)) return false
    if (
      filter.terminal?.length &&
      (!this.terminal || !filter.terminal.includes(this.terminal))
    ) {
      return false
    }
    if (filter.container_numbers) {
      const parsedContainerNumbers = parseContainerNumbers(
        filter.container_numbers
      )
      if (!parsedContainerNumbers.includes(this.number)) return false
    }
    // TODO: filter.updated_in_last_minutes, although we would need to update the API
    // to return last_updated in the container model
    if (filter.shipping_lines) {
      if (!this.line) return false
      if (!filter.shipping_lines.includes(this.line)) return false
    }
    if (filter.container_types) {
      if (!this.type) return false
      if (!filter.container_types.includes(this.type)) return false
    }
    const assigned_user_ids = filter.assigned_user_ids
    if (assigned_user_ids) {
      // -1 means filter to unassigned containers (i.e. no assigned users)
      const matchesUnassigned =
        assigned_user_ids.includes(-1) && this.assigned_users.length === 0
      const matchesAssignedUser = this.assigned_users.some((user) =>
        assigned_user_ids.includes(user.id)
      )
      if (!matchesUnassigned && !matchesAssignedUser) {
        return false
      }
    }
    if (filter.auto_book_off !== undefined) {
      // TODO Fix this criteria to match API
      if (filter.auto_book_off === this.auto_book_on) return false
    }
    if (filter.auto_book_on !== undefined) {
      // TODO Fix this criteria to match API
      if (filter.auto_book_on !== this.auto_book_on) return false
    }
    if (filter.excluded_customer_ids?.length) {
      const effectiveCustomerId = this.customer_id ?? NO_CUSTOMER_ID
      if (filter.excluded_customer_ids.includes(effectiveCustomerId))
        return false
    }
    // Unlike the green-truck filter, this one needs no store: the line's gap config
    // was handed to the container at construction, so a live update can answer it.
    if (filter.needs_line_lfd_check && !this.needsLineLFDCheck) return false
    if (filter.show_not_tagged === true) {
      if (this.tags.length > 0) return false
    } else if (filter.tags !== undefined && filter.tags.length > 0) {
      let hasTagMatch = false
      for (const tag of filter.tags) {
        if (this.tags.includes(tag)) {
          hasTagMatch = true
          break
        }
      }
      if (!hasTagMatch) return false
    }
    return true
  }

  incrementFrontendVersion(version?: number): void {
    this.frontendVersion = version ?? 0
    this._updateKey()
  }

  _updateKey(): string {
    this.key = `${this.number}-${this.frontendVersion}`
    return this.key
  }
}
